import { TextField } from "@mui/material";
import { makeStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { AppDialog } from "../components/AppDialog";
import { AppTextEditor } from "../components/AppTextEditor";
import { AppToolbar } from "../components/AppToolbar";
import { SimpleAutoComplete } from "../components/SimpleAutoComplete";
import { SelectFieldComponent, TextFieldComponent } from "../external/ensolvers-core-frontend-mui/components/Common/FormComponents";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import useKnowledgeBaseList from "../hooks/useKnowledgeBaseList";
import KnowledgeBase from "../models/KnowledgeBase";
import useTableHeight from "../hooks/useTableHeight";

const categories = [
  {
    value: "React - Good Practices",
    label: "React - Good Practices",
  },
  {
    value: "Spring Boot - Good Practices",
    label: "Spring Boot - Good Practices",
  },
  {
    value: "React - Bad Smells",
    label: "React - Bad Smells",
  },
  {
    value: "Spring Boot - Bad Smells",
    label: "Spring Boot - Bad Smells",
  },
  {
    value: "Node.js - Good Practices",
    label: "Node.js - Good Practices",
  },
  {
    value: "Node.js - Bad Smells",
    label: "Node.js - Bad Smells",
  },
];

export default function KnowledgeBaseList() {
  const { t } = useTranslation();
  const classes = useStyles();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();

  const {
    searchAll,
    knowledgeBaseCategories,
    searchCategory,
    onChangeCategory,
    searchCode,
    onChangeCode,
    onDataFetch,
    forceUpdateOn,
    onCreate,
    onUpdate,
    onEdit,
    onCancel,
    hideActions,
    onEditContentLink,
    setOpenDetails,
    setSelectedKnowledgeBase,
    setOpenDelete,
    openDelete,
    selectedKnowledgeBase,
    handleOnDeleteKnowledgeBase,
    knowledgeBaseCodes,
    openDetails,
    saveContentLink,
    handleCloseDetails,
    authorizedRole,
  } = useKnowledgeBaseList();

  return (
    <>
      <AppContainerFullHeight
        bottomSectionRef={bottomSectionRef}
        topSection={
          <>
            <AppToolbar title={t("Knowledge Base")}>
              <div className={classes.container}>
                <SimpleAutoComplete
                  label="Category"
                  items={[searchAll, ...knowledgeBaseCategories]}
                  value={searchCategory}
                  onChange={onChangeCategory}
                />
                <SimpleAutoComplete label="Code" items={[searchAll, ...knowledgeBaseCodes]} value={searchCode} onChange={onChangeCode} />
              </div>
            </AppToolbar>
          </>
        }
        bottomSection={
          <TableBasedCrud<KnowledgeBase>
            maxHeight={calculateMaxHeight(authorizedRole.valueOf())}
            stickyHeader
            elementName={t("Knowledge Base")}
            onDataFetch={onDataFetch}
            disableAutoFetch
            forceUpdateOn={forceUpdateOn}
            denseTable
            onCreate={onCreate}
            onUpdate={onUpdate}
            onClickEdit={onEdit}
            onCancelDialog={onCancel}
            hideActions={hideActions}
            renderDialogContent={(register, errors, control) => (
              <>
                <SelectFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"category"}
                  label="Category"
                  required={true}
                  options={categories}
                />
                <TextFieldComponent propertyName={"title"} label="Title" required={true} />
                <TextFieldComponent propertyName={"description"} label="Description" required={true} />

                <AppTextEditor label={"Content link"} defaultValue={onEditContentLink} onChange={saveContentLink} />
              </>
            )}
            tableColumns={[
              {
                header: t("Category"),
                content: (instance) => <>{instance.category}</>,
              },
              {
                header: t("Code"),
                content: (instance) => <>{instance.code}</>,
              },
              {
                header: t("Title"),
                content: (instance) => (
                  <Link
                    to={"#"}
                    onClick={() => {
                      setOpenDetails(true);
                      setSelectedKnowledgeBase(instance);
                    }}
                  >
                    {instance.title}
                  </Link>
                ),
              },
              {
                header: t("Description"),
                content: (instance) => <>{instance.description}</>,
              },
            ]}
            buildAdditionalActions={(instance, refresh) => {
              return [
                {
                  title: t("Delete"),
                  action: () => {
                    setSelectedKnowledgeBase(instance);
                    setOpenDelete(true);
                  },
                },
              ];
            }}
            validateBeforeClosing={true}
          />
        }
      />

      {/* Dialog to delete positions */}
      {openDelete && (
        <AppDialog
          open={!!selectedKnowledgeBase}
          onClose={() => {
            setSelectedKnowledgeBase(undefined);
            setOpenDelete(false);
          }}
          title={"Delete"}
          content={
            <Typography>
              {`The next Knowledge Base is going to be eliminated: ${selectedKnowledgeBase?.title} - ${selectedKnowledgeBase?.code}. 
            Do you want to confirm this action?`}
            </Typography>
          }
          buttons={[
            {
              label: "Confirm delete",
              onClick: () => {
                handleOnDeleteKnowledgeBase();
                setOpenDelete(false);
              },
            },
          ]}
        />
      )}

      {/* Dialog to show Knowledge Base details */}
      <AppDialog
        open={!!openDetails}
        onClose={handleCloseDetails}
        title={"Knowledge"}
        content={
          <Grid container display={"flex"} alignItems={"center"} justifyItems={"center"}>
            <TextField disabled autoFocus margin="dense" id="code" label="Code" fullWidth value={selectedKnowledgeBase?.code} />
            <TextField disabled autoFocus margin="dense" id="category" label="Category" fullWidth value={selectedKnowledgeBase?.category} />
            <TextField disabled autoFocus margin="dense" id="title" label="Title" fullWidth value={selectedKnowledgeBase?.title} />
            <TextField
              disabled
              autoFocus
              margin="dense"
              id="description"
              label="Description"
              fullWidth
              value={selectedKnowledgeBase?.description}
            />

            <AppTextEditor label={"Content link"} defaultValue={selectedKnowledgeBase?.contentLink} readOnly />
          </Grid>
        }
        hideCancel
        buttons={[{ label: "Close", onClick: () => handleCloseDetails() }]}
      />
    </>
  );
}

const useStyles = makeStyles(
  () => ({
    container: {
      width: "100%",
      display: "flex",
      padding: "5px",
      columnGap: "10px",
      "& > div": {
        margin: "0",
        width: "100%",
        maxWidth: "250px",
        minWidth: "0",
      },
    },
  }),
  { index: 1 },
);
